/**
 * Capitalizes the first letter of a string and makes the rest lowercase
 * @param str The string to transform
 * @returns The transformed string with first letter capitalized
 */
export const capitalizeFirstLetter = (str: string): string => {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

/**
 * Capitalizes the first letter of each word in a string
 * @param str The string to transform
 * @returns The transformed string with first letter of each word capitalized
 * @example
 * capitalizeEachWord("hola mundo") // returns "Hola Mundo"
 * capitalizeEachWord("buenos días señor") // returns "Buenos Días Señor"
 */
export const capitalizeEachWord = (str: string): string => {
  if (!str) return '';
  return str
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

/**
 * Converts a slug string to normal text with spaces
 * @param slug The slug string to convert
 * @returns The converted string with hyphens/underscores replaced by spaces
 * @example
 * slugToText("hello-world") // returns "hello world"
 * slugToText("contact_us_now") // returns "contact us now"
 */
export const slugToText = (slug: string): string => {
  if (!slug) return '';
  return slug.replace(/[-_]/g, ' ');
};

/**
 * Formats a number or string as currency with the specified currency symbol
 * @param value The number or string to format
 * @param currencySymbol The currency
 * @returns The formatted currency string or "Contáctanos" for null/undefined/empty/zero values
 * @example
 * formatCurrency("90400.00") // returns "$90,400.00 USD"
 * formatCurrency(0) // returns "Contáctanos"
 */
export const formatCurrency = (value: string | number, currencySymbol: string): string => {
  if (!value) return '$ Contáctanos';

  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(numValue) || numValue === 0) return 'Contáctanos';

  const formattedValue = numValue.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });

  return `$${formattedValue} ${currencySymbol}`;
}; 